import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import getColor from "@insite/mobius/utilities/getColor";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridItem, Typography } from "@insite/mobius/index";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";

const mapStateToProps = (state: ApplicationState) => ({
    settings: state.context.settings,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
    featureWidgets: state.context.session.properties?.featureWidgets?.split(",").map(page => page.trim()) || [],
});
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface ReportsStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    poLineTitleGridItem?: GridItemProps;
    packingSlipTitleGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    titleContainer?: GridItemProps;
    container?: GridContainerProps;
    reportDetailsGridItem?: GridItemProps;
    formatSelectionGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    button?: ButtonPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    reportTitle?: TypographyPresentationProps;
}

export const reportsStyles: ReportsStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                > div {
                    border-bottom: 1px solid lightgrey;
                    padding-bottom: 15px;
                    margin-bottom: 15px;
                    &:last-of-type {
                        border: none;
                        margin-bottom: 0;
                    }
                }
            `,
        },
    },

    centeringWrapper: {
        css: css`
            display: flex;
            height: 150px;
            justify-content: center;
            align-items: center;
        `,
    },

    titleContainer: {
        width: 12,
    },

    title: {
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.larger};
        `,
    },
    container: {
        css: css`
            flex-direction: column;
        `,
    },
    reportDetailsGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            flex-direction: column;
        `,
    },
    formatSelectionGridItem: {
        width: [12, 8, 2, 2, 2],
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;

            select {
                cursor: pointer;
            }
        `,
    },
    buttonGridItem: {
        width: [12, 4, 2, 2, 2],
        css: css`
            justify-content: right;
            margin-left: auto !important;
            align-items: center;
        `,
    },
    button: {
        buttonType: "outline",
        variant: "secondary",
        mergeCss: true,
        css: css`
            [class^="IconWrapper"] {
                color: ${getColor("primary")};
            }
        `,
    },
    reportTitle: {
        weight: "bold",
        color: "primary",
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            cursor: pointer;
        `,
    },
};

const styles = reportsStyles;

const CustomerForms: FC<Props> = ({ id, fields, settings, history, featureWidgets, isSecurityRolesAssigned }) => {
    const { links } = useGetLinks(fields.forms, (form: any) => form.fields.link);

    return (
        <Accordion {...styles.accordion} headingLevel={3}>
            <ManagedAccordionSection
                title={
                    <GridContainer>
                        <GridItem {...styles.titleContainer}>
                            <Typography as="h2" {...styles.title}>
                                {translate("Request Forms")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                }
                initialExpanded={true}
                {...styles.accordionSection}
            >
                {fields.forms.map((form: any, index: number) => {
                    const link = links[index];

                    const goToLink = () => {
                        const url = link?.url || "/";
                        if (form.fields.openInNewTab) {
                            window.open(url, "_blank", "noopener,noreferrer");
                        } else {
                            history.push(url);
                        }
                    };

                    return (
                        <GridContainer key={form.title} {...styles.container}>
                            <GridItem {...styles.reportDetailsGridItem}>
                                <Typography as="h2" {...styles.reportTitle} onClick={goToLink}>
                                    {form.fields.title}
                                </Typography>
                                <Typography as="p">{form.fields.description}</Typography>
                            </GridItem>

                            <GridItem {...styles.buttonGridItem}>
                                <Button {...styles.button} onClick={goToLink}>
                                    {translate("View Form")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    );
                })}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CustomerForms)),
    definition: {
        displayName: "Customer Forms",
        group: "Common",
        fieldDefinitions: [
            {
                name: "forms",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => item.fields.title,
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
